import { ListItemBadgeVariant, Spinner, SpinnerSize } from "@next-platform/client-common-spicy-ui";
import { NotificationType, SortOptions, UserVoice, UserVoiceActions, useTranslation, useUserVoices } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import { TEN_SECONDS } from "@/constants";
import useToast from "@/hooks/useToast";
import { OutletContextType } from "@/pages/Creations/CreationLayout";
import getVoiceStatus from "@/pages/Voice/getVoiceStatus";
import VoiceListContainer from "@/pages/Voice/VoiceListContainer";

const produceFilteredVoices = (search: string, userVoiceData: UserVoice[]) => {
  return search === ""
    ? userVoiceData.slice()
    : userVoiceData.filter((voice: UserVoice) =>
        [voice.name?.toLowerCase(), voice.description?.toLowerCase()].some((field) => field?.includes(search.toLowerCase())),
      );
};

export default function MyVoicesPage() {
  const { t } = useTranslation();
  const { userVoices, getUserVoices, isLoading, loadingType, deleteUserVoice } = useUserVoices();

  const { displayToast } = useToast();
  const refWasGenerating = useRef(false);
  const refGeneratingIds = useRef<Set<string>>(new Set());

  const [stateShowDeleteConfirmationDialog, setStateShowDeleteConfirmationDialog] = useState(false);
  const [stateVoiceToDelete, setStateVoiceToDelete] = useState<UserVoice | null>(null);
  const [search, , sort] = useOutletContext<OutletContextType>();

  const filteredVoices = useMemo(() => {
    const processedVoices = produceFilteredVoices(search, userVoices);
    switch (sort) {
      case SortOptions.OLDEST:
        processedVoices.sort(
          (voice1: UserVoice, voice2: UserVoice) => new Date(voice1.createdAt ?? 0).getTime() - new Date(voice2.createdAt ?? 0).getTime(),
        );
        break;
      case SortOptions.LATEST:
        processedVoices.sort(
          (voice1: UserVoice, voice2: UserVoice) => new Date(voice2.createdAt ?? 0).getTime() - new Date(voice1.createdAt ?? 0).getTime(),
        );
        break;
    }
    return processedVoices;
  }, [search, sort, userVoices]);

  useEffect(() => {
    getUserVoices();
  }, [getUserVoices]);

  useEffect(() => {
    const currentGeneratingIds = new Set(
      userVoices.filter((voice) => voice.status && getVoiceStatus(voice.status) === ListItemBadgeVariant.GENERATING).map((voice) => voice.id),
    );
    const hasPending = currentGeneratingIds.size > 0;

    if (refWasGenerating.current && !hasPending) {
      const prevGeneratingIds = refGeneratingIds.current;
      const anySucceeded = userVoices.some(
        (voice) => prevGeneratingIds.has(voice.id) && voice.status && getVoiceStatus(voice.status) !== ListItemBadgeVariant.ERROR,
      );
      const anyFailed = userVoices.some(
        (voice) => prevGeneratingIds.has(voice.id) && voice.status && getVoiceStatus(voice.status) === ListItemBadgeVariant.ERROR,
      );
      if (anySucceeded) {
        displayToast({ message: "Your custom voice has finished generating!", type: NotificationType.success });
      }
      if (anyFailed) {
        displayToast({ message: "Your custom voice failed to generate.", type: NotificationType.error });
      }
    }

    refWasGenerating.current = hasPending;
    refGeneratingIds.current = currentGeneratingIds;

    if (!hasPending) return;

    const id = setInterval(() => {
      getUserVoices();
    }, TEN_SECONDS);

    return () => clearInterval(id);
  }, [userVoices, getUserVoices, displayToast]);

  const handleClose = useCallback(() => {
    setStateShowDeleteConfirmationDialog(false);
    setStateVoiceToDelete(null);
  }, []);

  const handleDeleteVoice = useCallback((voice: UserVoice) => {
    setStateVoiceToDelete(voice);
    setStateShowDeleteConfirmationDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (stateVoiceToDelete) {
      await deleteUserVoice(stateVoiceToDelete.id);
      handleClose();
    }
  }, [deleteUserVoice, handleClose, stateVoiceToDelete]);

  if (isLoading && userVoices.length === 0) {
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <Spinner size={SpinnerSize.LARGE} className="mx-auto" />
      </div>
    );
  }

  return (
    <>
      <VoiceListContainer search={search} voices={filteredVoices} onDeleteVoice={handleDeleteVoice} />
      {stateShowDeleteConfirmationDialog ? (
        <ConfirmationModal
          heading={t("myCreation:modal.removeVoice.title")}
          handleClose={handleClose}
          isLoading={loadingType === UserVoiceActions.DELETE_USER_VOICE && isLoading}
          onUserConfirmation={confirmDelete}
          confirmationMessage={t("myCreation:modal.removeVoice.description", { name: stateVoiceToDelete?.name })}
          isVisible={stateShowDeleteConfirmationDialog}
          actionButtonColor="error"
          actionButtonText={t("common:remove")}
          discardButtonText={t("common:cancel")}
        />
      ) : null}
    </>
  );
}
